import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react'
import { IntlProvider } from 'react-intl'
import {defaultRichTextElements, Languages} from '../../../translations/config'
import {findLanguageConfig, getPreselectedLanguage, useSelectedLanguage, useSwitchLanguage} from '../../../translations/utils'

type TranslationContextValue = {
  selectedLanguage: Languages,
  switchLanguage: (language: Languages) => void
}

const TranslationContext = createContext<TranslationContextValue>({selectedLanguage: Languages.ptPT, switchLanguage: () => {}})

function useDefaultLanguage() {
  const switchLanguage = useSwitchLanguage()
  const selectedLanguage = useSelectedLanguage()

  useEffect(() => {
    const languageConfig = findLanguageConfig(selectedLanguage)

    if (!languageConfig) {
      switchLanguage(getPreselectedLanguage())
    }
  }, [selectedLanguage, switchLanguage])
}

export function TranslationProvider({children}: {children: ReactNode}) {
  const switchLanguage = useSwitchLanguage()
  const selectedLanguage = useSelectedLanguage()
  useDefaultLanguage()

  const value = useMemo<TranslationContextValue>(() => ({selectedLanguage, switchLanguage}), [selectedLanguage, switchLanguage])

  if (!selectedLanguage) return null

  return (
    <TranslationContext.Provider value={value}>
      <IntlProvider
        locale={selectedLanguage}
        messages={findLanguageConfig(selectedLanguage)?.translations}
        defaultRichTextElements={defaultRichTextElements}
        defaultLocale="pt-PT"
      >
        {children}
      </IntlProvider>
    </TranslationContext.Provider>
  )
}

export function useTranslation() {
  const {selectedLanguage, switchLanguage} = useContext(TranslationContext)
  return {selectedLanguage, switchLanguage}
}
